import { createGlobalStyle, css, type DefaultTheme } from 'styled-components';
import 'normalize.css';

import Colours, { getColourVariables } from '/shared/styled/Colours';
import fontsStyle from '/shared/styled/Fonts';
import '/shared/fonts/BpSans/stylesheet.css';

interface GlobalStyleProps {
  theme: DefaultTheme;
  isDebug?: boolean;
}

function debugStyle({ theme, isDebug = false }: GlobalStyleProps) {
  if (theme?.isDebug || isDebug) {
    return css`
      * {
        outline: 1px solid ${Colours.redOverlay};
      }
    `;
  }
}

function isMouseStyle({ theme }: GlobalStyleProps) {
  if (!theme?.isDebug) {
    return css`
      html.is-mouse {
        * {
          outline: none;
        }
      }
    `;
  }
}

export const baseFontWeight = css`
  font-weight: 300;
`;

function calculatorStyle() {
  return css`
    display: flex;
    flex-direction: column;
    background: ${Colours.white};
    font-family: 'BpSans', sans-serif;
    color: ${Colours.black};
    font-size: 15px;
    line-height: 1.5;
    /* letter-spacing: 0.15px; */
    -webkit-font-smoothing: antialiased;
    font-kerning: normal;
    text-rendering: geometricprecision !important;

    ${({ theme }) =>
      !theme?.target?.isConsumer &&
      css`
        height: 100vh;
      `}
    strong {
      font-weight: 400;
    }

    img {
      display: block;
    }

    pre {
      overflow: scroll;
      word-wrap: break-all;
      font-size: 10px;
      background: ${Colours.greyLight8};
      padding: 1rem;
    }

    a:visited {
      color: inherit;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }

    input[type=number] {
      appearance: textfield;

      ::-ms-clear {
        display: none;
      }
    }
    ${baseFontWeight}
    ${debugStyle}
  `;
}

// Apply calculator styles to the main html and body if under a test or CI
// environment, which doesn't use an inner DOM element to inject the app.
// Additionally, load the fonts via a StyledComponent. This is unfortunately
// really buggy and loads the fonts on every render when accessing it via the
// browser. No idea why.
function isCIStyle() {
  if (['test', 'ci'].indexOf(import.meta.env?.NODE_ENV?.toLowerCase()) > -1) {
    return css`
      html, body {
        ${calculatorStyle}
        ${fontsStyle}
        display: block;
      }
    `;
  }
}

export default createGlobalStyle`
  :root {
    ${({ theme }) => getColourVariables(theme)}
  }

  *, *::after, &::before {
    box-sizing: border-box;
    position: relative;
  }

  html, body {
    font-size: 15px;
  }

  html {
    margin: 0;
    padding: 0;
    transition: 1s ease-out all;
    /* scroll-behavior: smooth; */
    &.hard-reset {
      transform: rotate(1080deg) scale(0);
      opacity: 0;
    }
  }

  body {
    margin: 0;
  }

  .nr-layout-component {
    padding-top: 0 !important;
  }

  .nr-layout__wrapper {
    padding: 0;

    .nr-component, .nr-nav-app-component {
      margin: 0;
      padding: 0;
    }
  }

  .content .carbon-calculator {
    a {
      color: inherit;
    }

    a:visited {
      color: inherit;
    }
  }

  .carbon-calculator {
    ${calculatorStyle}
  }

  .main {
    overflow-x: hidden;
  }

  @media (width <= 600px) {
    .sidebar {
      display: none;
    }

    .main {
      width: 100%;
    }

    .heading {
      margin-right: 0;
    }
  }

  @media screen and (width <= 1023px) {
    .nr-breadcrumb, .nr-header {
      display: none;
    }
  }
  ${isMouseStyle}
  ${isCIStyle}
`;
